// 순수한둠비 취합 주문서 → 정산 결과(날짜|이름|품목|수량) 변환 순수 함수.
// 입력은 '저녁이있는엑셀(취합)' 시트를 AOA(any[][])로 읽은 것. 헤더 행 없이 데이터로 시작.

// 취합 시트의 고정 열 위치(0-index). 매크로가 항상 같은 열에 붙여넣는다.
export const COL = {
  fileName: 0, // A: 파일명 (날짜 추출)
  name: 7,     // H: 고객명
  phone: 8,    // I: 전화번호
  address: 12, // M: 주소
  items: 14,   // O: 품목 ("/" 구분, "N개" 포함)
} as const;

export interface OutputRow {
  date: string; // MM/DD (공란 연속 행은 '')
  name: string; // (공란 연속 행은 '')
  item: string;
  qty: number;
  homonym: boolean; // 이름은 같지만 전화·주소가 달라 다른 사람이 존재(동명이인) — 이름 표시 행에만 true
}

// 파일명/날짜 문자열에서 YYYY.M.D 를 찾아 MM/DD 텍스트로.
export function parseDate(fileName: string): string {
  const m = String(fileName || '').match(/(\d{4})\.(\d{1,2})\.(\d{1,2})/);
  if (!m) return '';
  const mm = m[2].padStart(2, '0');
  const dd = m[3].padStart(2, '0');
  return `${mm}/${dd}`;
}

// "단호박 두부 1개 / 순수한 두부(주먹두부) 1개" → [{name,qty}, ...]
export function parseItems(cell: string): { name: string; qty: number }[] {
  return String(cell || '')
    .split('/')
    .map(s => s.trim())
    .filter(Boolean)
    .map(seg => {
      const m = seg.match(/(\d+)\s*개\s*$/);
      if (m) {
        const name = seg.slice(0, m.index).trim();
        return { name, qty: Number(m[1]) };
      }
      return { name: seg, qty: 1 };
    });
}

function key(date: string, name: string, phone: string, address: string): string {
  return [date, name, phone, address].map(s => String(s || '').trim()).join('');
}

export function transformRows(aoa: any[][]): OutputRow[] {
  // 동일인(날짜+이름+전화+주소) 그룹을 첫 등장 순서로 유지하며 품목을 병합.
  // identity(전화+주소)는 날짜와 무관한 '사람' 식별자 — 동명이인 판정에 사용.
  const order: string[] = [];
  const groups = new Map<string, { date: string; name: string; identity: string; items: { name: string; qty: number }[] }>();

  for (const r of aoa) {
    if (!r) continue;
    const name = String(r[COL.name] ?? '').trim();
    const itemsCell = String(r[COL.items] ?? '').trim();
    if (!name || !itemsCell) continue; // 이름·품목 없는 행 스킵

    const date = parseDate(String(r[COL.fileName] ?? ''));
    const phone = String(r[COL.phone] ?? '').trim();
    const address = String(r[COL.address] ?? '').trim();
    const k = key(date, name, phone, address);

    let g = groups.get(k);
    if (!g) {
      g = { date, name, identity: `${phone}|${address}`, items: [] };
      groups.set(k, g);
      order.push(k);
    }
    g.items.push(...parseItems(itemsCell));
  }

  // 동명이인 판정: 같은 이름이 서로 다른 '사람'(전화+주소)에 2개 이상 매핑되면 동명이인.
  // 같은 사람이 다른 날 주문한 경우(날짜만 다름)는 동명이인이 아니다.
  const identitiesByName = new Map<string, Set<string>>();
  for (const k of order) {
    const g = groups.get(k)!;
    if (!identitiesByName.has(g.name)) identitiesByName.set(g.name, new Set());
    identitiesByName.get(g.name)!.add(g.identity);
  }

  const out: OutputRow[] = [];
  for (const k of order) {
    const g = groups.get(k)!;
    const isHomonym = (identitiesByName.get(g.name)?.size ?? 0) > 1;
    g.items.forEach((it, i) => {
      out.push({
        date: i === 0 ? g.date : '',
        name: i === 0 ? g.name : '',
        item: it.name,
        qty: it.qty,
        homonym: i === 0 ? isHomonym : false,
      });
    });
  }
  return out;
}

// 원본 품목 총개수(모든 O조각) vs 출력 품목 행수 대조.
export function verify(aoa: any[][], out: OutputRow[]): { ok: boolean; issues: string[] } {
  const issues: string[] = [];
  let srcItems = 0;
  for (const r of aoa) {
    if (!r) continue;
    const name = String(r[COL.name] ?? '').trim();
    const itemsCell = String(r[COL.items] ?? '').trim();
    if (!name || !itemsCell) continue;
    srcItems += parseItems(itemsCell).length;
  }
  if (srcItems !== out.length) {
    issues.push(`원본 품목 ${srcItems}개 ↔ 출력 ${out.length}행 불일치`);
  }
  return { ok: issues.length === 0, issues };
}
